import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ArrowLeftIcon } from "./Icons.tsx";

/**
 * The top of every page in the desktop shell.
 *
 * Title on the left, actions on the right, an optional one-line description under the title. The
 * business name is not repeated here because the sidebar already says it; `eyebrow` exists for the
 * pages that need a category above the title. `back` is the one-level breadcrumb a pushed page
 * (a thread under Inbox) uses to get back to its list.
 *
 * Not sticky. The sidebar and the top bar are the chrome that stays; the title is read once.
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  back,
}: {
  title: string;
  /** Words, or words with small parts in them (a coach's face next to the name). */
  description?: ReactNode;
  eyebrow?: string;
  /** Buttons, right of the title at desktop and under it on a phone. */
  actions?: ReactNode;
  back?: { to: string; label: string; params?: Record<string, string> };
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {back ? (
          <Link
            to={back.to}
            params={back.params}
            className="-ml-1 mb-2 inline-flex min-h-9 items-center gap-1.5 pr-2 text-[14px] font-semibold text-accent-strong"
          >
            <ArrowLeftIcon size={18} />
            {back.label}
          </Link>
        ) : null}
        {eyebrow ? <p className="eyebrow mb-1.5 truncate">{eyebrow}</p> : null}
        <h1 className="text-[28px] lg:text-[32px]">{title}</h1>
        {description ? <p className="mt-1.5 text-[15px] text-ink-2">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}
